// AdministrativeAreas query — the whole project tree as a FLAT list (each node carries its
// parent_id); the page assembles the nested tree for the expandable DataTable. One
// cached query keyed by project; mutations invalidate it. There is no detail
// endpoint — the peek reads its node from this cached list (the list row is the
// whole node), mirroring site-types' no-detail shape. The read is member-level
// (every member names every area — the define-vs-use split; defining stays
// `reference_data:manage`).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient.js';
import type { AdministrativeAreasListResponse, AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';
import type { OptionRow, ParentedSource, SmallSource } from './useOptions.js';

export const administrativeAreasKeys = {
  all: (projectId: string) => ['administrative-areas', projectId] as const,
};

/** The tree read — the page's, and a form's Load the latest (an area has no detail read: its node in the list is its
 *  read). */
export async function fetchAdministrativeAreas(projectId: string): Promise<AdministrativeAreaNode[]> {
  const { data } = await apiClient.get<AdministrativeAreasListResponse>(`/projects/${projectId}/administrative-areas`);
  return data.items;
}

export function useAdministrativeAreas(projectId: string): UseQueryResult<AdministrativeAreaNode[], Error> {
  return useQuery({
    queryKey: administrativeAreasKeys.all(projectId),
    queryFn: () => fetchAdministrativeAreas(projectId),
    enabled: projectId.length > 0,
  });
}

/** The areas as rows (ADR-080 §3 · §14 — hosted on the NAVIGATOR): each carries the area it sits directly `inside`
 *  (the level walked one at a time; `null` at a country's top), its `path` (the names above it, top first — under a
 *  search match and under the chosen value) and its COUNTRY as its `parent` mark; a disabled area is an inactive
 *  row. One country's rows run together (a filter's top level lists each country's tops in turn, never interleaved);
 *  inside a country the backend's curated order (display_order within each level) is kept. */
function areaRows(nodes: AdministrativeAreaNode[]): OptionRow[] {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  nodes = [...nodes].sort((a, b) => a.country_iso_code.localeCompare(b.country_iso_code));
  const pathOf = (n: AdministrativeAreaNode): string => {
    const names: string[] = [];
    const seen = new Set<number>([n.id]);
    let cur = n.parent_id;
    while (cur !== null && !seen.has(cur)) {
      const p = byId.get(cur);
      if (!p) break;
      names.unshift(p.name);
      seen.add(cur);
      cur = p.parent_id;
    }
    return names.join(' / ');
  };
  return nodes.map((n) => ({
    id: String(n.id),
    label: n.name,
    pickable: true,
    inactive: n.is_disabled,
    group: null,
    parent: n.country_iso_code,
    inside: n.parent_id === null ? null : String(n.parent_id),
    path: pathOf(n) || undefined,
  }));
}

/** The areas as a FILTER's source (the small kind on the list's own key, the whole tree on the press): every
 *  country's areas, the countries' tops together at the top level. */
export function administrativeAreasSource(projectId: string): SmallSource<AdministrativeAreaNode[]> {
  return { kind: 'small', queryKey: administrativeAreasKeys.all(projectId), fetch: () => fetchAdministrativeAreas(projectId), rows: areaRows };
}

/** The areas as a FIELD's source — the same rows under their COUNTRY (the parent the field declares, ADR-080 §9):
 *  the site form offers the site's country's areas alone and clears the pick when the country changes
 *  (`must_belong`). */
export function administrativeAreasByCountrySource(projectId: string): ParentedSource<AdministrativeAreaNode[]> {
  return { kind: 'small', queryKey: administrativeAreasKeys.all(projectId), fetch: () => fetchAdministrativeAreas(projectId), rows: areaRows, parented: true };
}
